import { Button, Css, IconKey } from "@homebound/beam";
import { useEffect, useState } from "react";
import { BannerNotice } from "src/components";
import { useBillIntacctSyncErrorQuery, useSyncBillToIntacctMutation } from "src/routes/bills/BillPage.generated";
type BannerSyncProps = { billId: string | null | undefined; hasSyncError: boolean };

type BillBannerProps = { message?: string; icon?: IconKey; success?: boolean; show: boolean };

export function BillBanner({ billId, hasSyncError }: BannerSyncProps) {
  const [syncBillToIntacct] = useSyncBillToIntacctMutation();
  const [isSyncLoading, setIsSyncLoading] = useState(false);
  const querySyncError = useBillIntacctSyncErrorQuery({ variables: { billId: billId! }, skip: !billId });
  const [bannerProps, setBannerProps] = useState<BillBannerProps | undefined>(
    hasSyncError
      ? {
          message: `Bill ${billId} has failed to sync with ERP system. Fix related issues and try again.`,
          icon: "xCircle",
          success: false,
          show: true,
        }
      : { show: false },
  );

  useEffect(() => {
    if (
      querySyncError?.previousData &&
      querySyncError?.previousData?.bill?.updatedAt !== querySyncError.data?.bill?.updatedAt
    ) {
      const successSync = !querySyncError?.data?.bill?.intacctSyncError;
      querySyncError.stopPolling();
      setIsSyncLoading(false);
      const newBannerProps: BillBannerProps = !successSync
        ? {
            message: `Sync with ERP system failed. Check Details section for the reason.`,
            icon: "xCircle",
            success: false,
            show: true,
          }
        : { message: `Successfully synced with ERP.`, icon: "checkCircle", success: true, show: true };
      setBannerProps(newBannerProps);
      if (successSync) {
        setTimeout(() => {
          // After 3 seconds hide the banner
          setBannerProps({ show: false });
        }, 3000);
      }
    }
  }, [querySyncError]);

  async function onSyncToErp() {
    try {
      await syncBillToIntacct({ variables: { input: { id: billId ?? "" } } });
      querySyncError.startPolling(1000);
      setIsSyncLoading(true);
    } catch {
      setIsSyncLoading(false);
    }
  }

  return (
    <div hidden={!bannerProps?.show} data-testid="divBanner">
      <BannerNotice
        data-testid="bannerNotice"
        icon={bannerProps?.icon}
        message={bannerProps?.message}
        xss={bannerProps?.success ? Css.bgGreen100.gray900.$ : Css.bgRed100.gray900.$}
        right={
          <span hidden={bannerProps?.success} css={Css.lh(0).$}>
            <Button
              disabled={isSyncLoading}
              icon="refresh"
              onClick={onSyncToErp}
              label={isSyncLoading ? "Syncing..." : "Resync"}
              variant="tertiaryDanger"
              data-testid="resyncButton"
            />
          </span>
        }
      />
    </div>
  );
}
